import { useGenericModal } from "src/components/reusable/components/GenericModal";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";
import { useApiRoutes } from "src/components/reusable/hooks/useApiRoutes";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useSSAuth } from "src/components/reusable/hooks/useSSAuth";
import { useTicketmaster } from "src/components/reusable/hooks/useTicketmaster";
import { findApiErrorMessage } from "src/components/reusable/utils/utils";
import { constants } from "src/shared/constants";
import { routes } from "src/shared/routes";
import { renderValidElement } from "src/utils/render-valid-element";

export function useMarketplaceSeller() {
  const router = useRouterExt();
  const teamConfig = useTeamConfig();
  const kycConfirmationDesc = teamConfig?.MARKETPLACE_GLOBAL_SETTINGS?.STRIPE_KYC_CONFIRMATION_MODAL_DESCRIPTION; // prettier-ignore
  const redirectWarnDesc = teamConfig?.MARKETPLACE_GLOBAL_SETTINGS?.MARKETPLACE_REDIRECT_WARN_MODAL_DESCRIPTION; // prettier-ignore
  const { TEAM_SCHEMA } = teamConfig;
  const { user } = useGlobalStore();
  const { persistSession } = useSSAuth();
  const {
    getMerchantSessionApi,
    getMerchantAccountDetailsApi,
    updateMerchantKycStatusApi,
  } = useApiRoutes();
  const genericModal = useGenericModal();
  const { connectTicketmaster } = useTicketmaster();

  const fireErrorModal = (error: any) => {
    genericModal.set({
      isOpen: true,
      hideTitleIcon: true,
      title: "Error",
      description: findApiErrorMessage(error),
      isConfirmButtonHidden: true,
      onClickClose: async () => genericModal.reset(),
    });
  };

  const fireKYCConfirmationModal = () => {
    return new Promise((resolve) => {
      genericModal.set({
        isOpen: true,
        hideTitleIcon: true,
        title: "Seller Onboarding",
        description: renderValidElement({
          element: kycConfirmationDesc,
          fallback: (
            <>
              You’re about to leave this page to complete your seller
              onboarding.
              <br />
              <br />
              To list your tickets, you’ll need to finish onboarding first.
              We’ll collect your name, address, phone number, and bank details
              so we can deposit your payout when your ticket sells.”
            </>
          ),
        }),
        closeButtonText: "Cancel",
        confirmButtonText: "Continue",
        closeButtonProps: { variant: "outlined" },
        onClickClose: async () => resolve("cancelled"),
        onClickConfirm: async () => resolve("confirmed"),
      });
    });
  };

  const marketplaceRedirectWarnModal = () => {
    return new Promise((resolve, reject) => {
      genericModal.set({
        isOpen: true,
        hideTitleIcon: true,
        title: "Student Marketplace",
        description: renderValidElement({
          element: redirectWarnDesc,
          fallback:
            "You’re entering a peer-to-peer marketplace where students can buy and sell tickets directly with each other.",
        }),
        closeButtonText: "Cancel",
        confirmButtonText: "Continue",
        closeButtonProps: { variant: "outlined" },
        onClickClose: async () => resolve("cancelled"),
        onClickConfirm: async () => resolve("confirmed"),
      });
    });
  };

  const createKycLinkCallbackUrl = (eventCode?: string) => {
    const callbackUrl = `${window.location.origin}/${TEAM_SCHEMA}/stripe_callback`;
    const url = new URL(callbackUrl);
    if (eventCode) {
      url.searchParams.set("eventCode", eventCode);
      url.searchParams.set("createListing", "1");
    }
    return url.toString();
  };

  const fireKycIncompleteModal = async () => {
    const linkCallbackUrl = createKycLinkCallbackUrl(
      router?.query?.eventCode as string
    );
    return genericModal.set({
      isOpen: true,
      title: "Seller Onboarding",
      description: (
        <>
          Your seller onboarding is incomplete. Please click
          &apos;Continue&apos; to complete your seller onboarding
        </>
      ),
      closeButtonText: "Cancel",
      confirmButtonText: "Continue",
      closeButtonProps: { variant: "outlined" },
      onClickConfirm: async () => {
        try {
          const response = await getMerchantSessionApi.post({
            linkCallbackUrl,
          });
          if (response?.accountLink) {
            window.location.replace(response?.accountLink?.url);
          }
        } catch (error) {
          console.log(error);
        } finally {
          genericModal.reset();
        }
      },
      onClickClose: async () => {
        await router.pushSchema(routes.marketplace_home);
        genericModal.reset();
      },
    });
  };

  const navigateToCreateListing = async (args?: {
    eventCode?: string | string[] | undefined;
    isWarnEnabled?: boolean;
  }) => {
    if (args?.isWarnEnabled) {
      const warningRes = await marketplaceRedirectWarnModal();
      if (warningRes === "cancelled") return;
    }
    const query = args?.eventCode ? { eventCode: args?.eventCode as string, createListing: "1" } : {}; // prettier-ignore
    const pathname = routes.marketplace_create_listing;
    return await router.pushSchema({ pathname, query });
  };

  const handleStripeConnectCallback = async () => {
    try {
      const merchantAccountId = router?.query?.accountId as string;
      const accountDetails = await getMerchantAccountDetailsApi.post({
        merchantAccountId,
      });

      const isPendingKYC = accountDetails?.requirements?.currently_due?.length > 0; // prettier-ignore
      if (isPendingKYC) return fireKycIncompleteModal();

      await updateMerchantKycStatusApi.post({
        teamSchema: TEAM_SCHEMA,
        merchantAccountId: router?.query?.accountId as string,
        isKYCDone: true,
      });

      const response = await getMerchantSessionApi.post();
      if (response.session) {
        persistSession(response.session);
        return await navigateToCreateListing({
          eventCode: router?.query?.eventCode,
        });
      }
    } catch (error) {
      console.log(error);
      fireErrorModal(error);
    }
  };

  const initSellerAuth = async (redirect?: {
    pathname?: string;
    query?: Record<string, string>;
  }) => {
    if (user) return;
    if (!redirect) redirect = {};
    const listingRoute = routes.marketplace_create_listing;
    if (!redirect?.pathname) redirect.pathname = listingRoute;
    return new Promise((resolve) => {
      connectTicketmaster({
        state: {
          authOrigin: constants.authOrigins.marketplaceCreateListingPage,
          redirectRoute: redirect,
        },
      });
    });
  };

  const handleGetMerchantSession = async (args: {
    eventCode?: string;
    isListingRedirectWarnEnabled?: boolean;
  }) => {
    try {
      const linkCallbackUrl = createKycLinkCallbackUrl(args?.eventCode);
      const response = await getMerchantSessionApi.post({ linkCallbackUrl });

      if (response?.session) {
        persistSession(response.session);
        return await navigateToCreateListing({
          eventCode: args?.eventCode,
          isWarnEnabled: args?.isListingRedirectWarnEnabled,
        });
      }

      if (response?.accountLink) {
        const kycConfirmation = await fireKYCConfirmationModal();
        if (kycConfirmation === "cancelled") return;
        window.location.href = response?.accountLink?.url;
      }
    } catch (err) {
      fireErrorModal(err);
    }
  };

  const handleBecomeSeller = async (args?: {
    eventCode?: string;
    isListingRedirectWarnEnabled?: boolean;
  }) => {
    if (user?.marketplace?.merchantAccountId) {
      return await navigateToCreateListing({
        eventCode: args?.eventCode,
        isWarnEnabled: args?.isListingRedirectWarnEnabled,
      });
    }

    if (!user?.marketplace?.merchantAccountId) {
      return await handleGetMerchantSession({
        eventCode: args?.eventCode,
        isListingRedirectWarnEnabled: args?.isListingRedirectWarnEnabled,
      });
    }
  };

  return {
    createKycLinkCallbackUrl,
    handleStripeConnectCallback,
    handleBecomeSeller,
    initSellerAuth,
    navigateToCreateListing,
    marketplaceRedirectWarnModal,
    fireKYCConfirmationModal,
  };
}
